import React, { useCallback, useEffect, useState } from 'react';

import powerMeter from '../../../main/assets/images/powerMeterV2/power-meter-visualisation.webp';
import powerMeterTablet from '../../../main/assets/images/powerMeterV2/power-meter-visualisation-tablet.webp';
import powerMeterProduct from '../../../main/assets/images/powerMeterV2/power-meter-product-visualisation.webp';

const objectPostitions = {
  leftSide: {
    linePosition: {
      x: 135,
      y: 110,
    },
    titlePosition: {
      x: 8,
      y: 14,
    },
    descriptionPosition: {
      x: 8,
      y: 27,
    },
  },
  rightSide: {
    linePosition: {
      x: 1050,
      y: 475,
    },
    titlePosition: {
      x: 62,
      y: 53,
    },
    descriptionPosition: {
      x: 72,
      y: 66,
    },
  },
  tablet: {
    leftSide: {
      linePosition: {
        x: 75,
        y: 90,
      },
      titlePosition: {
        x: 7,
        y: 15,
      },
      descriptionPosition: {
        x: 7,
        y: 28,
      },
    },
    rightSide: {
      linePosition: {
        x: 735,
        y: 370,
      },
      titlePosition: {
        x: 49,
        y: 53,
      },
      descriptionPosition: {
        x: 64,
        y: 66,
      },
    },
  },
  mobile: {
    imageContainer: {
      width: 70,
      height: 70,
      top: 50,
      left: 50,
      transform: 'translate(-50%, -50%)',
    },
    leftSection: {
      width: '100%',
    },
    rightSection: {
      width: '100%',
    },
  },
  wideScreen: {
    leftSide: {
      linePosition: {
        x: 180,
        y: 110,
      },
      titlePosition: {
        x: 10,
        y: 14,
      },
      descriptionPosition: {
        x: 10,
        y: 23.5,
      },
    },
    rightSide: {
      linePosition: {
        x: 1000,
        y: 475,
      },
      titlePosition: {
        x: 60,
        y: 53,
      },
      descriptionPosition: {
        x: 70,
        y: 62.5,
      },
    },
  },
};

const BREAKPOINT_WIDE_SCREEN = 2400;
const BREAKPOINT_DESKTOP = 1201;
const BREAKPOINT_TABLET = 991;

export const Visualizations = () => {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement;
      setImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    },
    [],
  );

  const getCoordinates = useCallback(
    (xPercent: number, yPercent: number) => {
      if (imageSize.width === 0 || imageSize.height === 0) {
        return { x: 0, y: 0 };
      }
      return {
        x: (xPercent / 100) * imageSize.width,
        y: (yPercent / 100) * imageSize.height,
      };
    },
    [imageSize],
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= BREAKPOINT_TABLET;
  const isTablet =
    windowWidth < BREAKPOINT_DESKTOP && windowWidth > BREAKPOINT_TABLET;
  const isWideScreen = windowWidth > BREAKPOINT_WIDE_SCREEN;

  const currentPositions = isTablet
    ? objectPostitions.tablet
    : isWideScreen
    ? objectPostitions.wideScreen
    : objectPostitions;

  return (
    <div
      className="power-meter-visual"
      style={{
        position: 'relative',
        maxHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '20px' : '0',
      }}
    >
      {isMobile ? (
        <>
          {/* Mobile Left Section (Top) */}
          <div
            style={{
              width: objectPostitions.mobile.leftSection.width,
              textAlign: 'left',
            }}
            className="power-meter-visual-left"
          >
            <p
              style={{
                color: 'white',
                textTransform: 'uppercase',
                marginBottom: '20px',
                width: '50%',
                lineHeight: 'normal',
              }}
              className="power-visual-left-title"
            >
              Signal Flexibility
            </p>
            <p
              style={{
                width: '50%',
                lineHeight: '140%',
                color: 'rgba(255, 255, 255, 0.70)',
              }}
              className={'power-visual-left-description'}
            >
              Supports continuous analog VTX signals and pulsed digital RC
              controller signals for full testing versatility.
            </p>
          </div>

          {/* Mobile Image (Center) */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={powerMeterProduct}
              alt="Power Meter Product"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              className="power-visual-center-image"
              onLoad={handleImageLoad}
            />
          </div>

          {/* Mobile Right Section (Bottom) */}
          <div
            className="power-meter-visual-right"
            style={{
              width: objectPostitions.mobile.rightSection.width,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <p
              style={{
                color: 'white',
                textTransform: 'uppercase',
                marginBottom: '20px',
                width: '70%',
                textAlign: 'right',
                lineHeight: 'normal',
              }}
              className="power-visual-right-title"
            >
              Accurate Measurements
            </p>
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.70)',
                textAlign: 'right',
                width: '50%',
                lineHeight: '140%',
              }}
              className="power-visual-right-description"
            >
              Ensure your VTX outputs a true 25mW and stays consistent
              throughout the race. Supports the global 25mW standard to
              guarantee compliance and prevent issues.
            </p>
          </div>
        </>
      ) : (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <img
            src={isTablet ? powerMeterTablet : powerMeter}
            alt="Power Meter Product"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
            }}
            onLoad={handleImageLoad}
          />
          {imageSize.width > 0 && (
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
              viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Left Side Path */}
              <g
                transform={`translate(${currentPositions.leftSide.linePosition.x}, ${currentPositions.leftSide.linePosition.y})`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="715"
                  height="12"
                  viewBox="0 0 715 12"
                  fill="none"
                >
                  <path
                    d={
                      isTablet
                        ? 'M505.333 6.07031C505.333 3.12479 502.946 0.736979 500 0.736979C497.054 0.736979 494.667 3.12479 494.667 6.07031C494.667 9.01583 497.054 11.4036 500 11.4036C502.946 11.4036 505.333 9.01583 505.333 6.07031ZM500 5.07031L8.74228e-08 5.07027L-8.74228e-08 7.07027L500 7.07031L500 5.07031Z'
                        : 'M714.333 6C714.333 3.05448 711.946 0.666667 709 0.666667C706.054 0.666666 703.667 3.05448 703.667 6C703.667 8.94552 706.054 11.3333 709 11.3333C711.946 11.3333 714.333 8.94552 714.333 6ZM709 6L709 5L8.74228e-08 4.99994L0 5.99994L-8.74228e-08 6.99994L709 7L709 6Z'
                    }
                    fill="url(#paint0_linear_2002_4356)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2002_4356"
                      x1="709"
                      y1="5.5"
                      x2="4.37113e-08"
                      y2="5.49994"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0.509" stopColor="#FF2238" />
                      <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </g>

              {/* Left Side Title */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.leftSide.titlePosition.x,
                    currentPositions.leftSide.titlePosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.leftSide.titlePosition.x,
                    currentPositions.leftSide.titlePosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '30%' : '20%'}
                  height="50%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      color: 'white',
                      fontSize: isTablet || isWideScreen ? '32px' : '42px',
                      textTransform: 'uppercase',
                      lineHeight: 'normal',
                    }}
                  >
                    Signal Flexibility
                  </p>
                </foreignObject>
              </g>

              {/* Left Side Description */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.leftSide.descriptionPosition.x,
                    currentPositions.leftSide.descriptionPosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.leftSide.descriptionPosition.x,
                    currentPositions.leftSide.descriptionPosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '30%' : '20%'}
                  height="50%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.70)',
                      fontSize: isTablet || isWideScreen ? '16px' : '18px',
                      lineHeight: '140%',
                    }}
                  >
                    Supports continuous analog VTX signals and pulsed digital RC
                    controller signals for full testing versatility.
                  </p>
                </foreignObject>
              </g>

              {/* Right Side Path */}
              <g
                transform={`translate(${currentPositions.rightSide.linePosition.x}, ${currentPositions.rightSide.linePosition.y})`}
              >
                {isTablet ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="404"
                    height="12"
                    viewBox="0 0 404 12"
                    fill="none"
                  >
                    <path
                      d="M0.666667 6.07031C0.666667 9.01583 3.05448 11.4036 6 11.4036C8.94552 11.4036 11.3333 9.01583 11.3333 6.07031C11.3333 3.12479 8.94552 0.736979 6 0.736979C3.05448 0.736979 0.666666 3.12479 0.666667 6.07031ZM6 7.07031L404 7.07028L404 5.07028L6 5.07031L6 7.07031Z"
                      fill="url(#paint0_linear_1000_5240)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_1000_5240"
                        x1="6"
                        y1="6.57031"
                        x2="404"
                        y2="6.57028"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.509" stopColor="#FF2238" />
                        <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="715"
                    height="12"
                    viewBox="0 0 715 12"
                    fill="none"
                  >
                    <path
                      d="M0.666667 6.00025C0.666667 8.94576 3.05448 11.3336 6 11.3336C8.94552 11.3336 11.3333 8.94576 11.3333 6.00024C11.3333 3.05472 8.94552 0.66691 6 0.666911C3.05448 0.666911 0.666666 3.05473 0.666667 6.00025ZM6 6.00024L6 7.00024L715 7.00012L715 6.00012L715 5.00012L6 5.00024L6 6.00024Z"
                      fill="url(#paint0_linear_2002_4363)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_2002_4363"
                        x1="6"
                        y1="6.50024"
                        x2="715"
                        y2="6.50012"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.509" stopColor="#FF2238" />
                        <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}
              </g>

              {/* Right Side Title */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.rightSide.titlePosition.x,
                    currentPositions.rightSide.titlePosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.rightSide.titlePosition.x,
                    currentPositions.rightSide.titlePosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '45%' : '30%'}
                  height="50%"
                  style={{
                    pointerEvents: 'none',
                    textTransform: 'uppercase',
                    textAlign: 'right',
                  }}
                >
                  <p
                    style={{
                      color: 'white',
                      fontSize: isTablet || isWideScreen ? '32px' : '42px',
                      lineHeight: 'normal',
                    }}
                  >
                    Accurate Measurements
                  </p>
                </foreignObject>
              </g>

              {/* Right Side Description */}
              <g
                transform={`translate(${
                  getCoordinates(
                    currentPositions.rightSide.descriptionPosition.x,
                    currentPositions.rightSide.descriptionPosition.y,
                  ).x
                }, ${
                  getCoordinates(
                    currentPositions.rightSide.descriptionPosition.x,
                    currentPositions.rightSide.descriptionPosition.y,
                  ).y
                })`}
              >
                <foreignObject
                  width={isTablet ? '30%' : '20%'}
                  height="50%"
                  style={{ pointerEvents: 'none' }}
                >
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.70)',
                      fontSize: isTablet || isWideScreen ? '16px' : '18px',
                      textAlign: 'right',
                      lineHeight: '140%',
                    }}
                  >
                    Ensure your VTX outputs a true 25mW and stays consistent
                    throughout the race. Supports the global 25mW standard to
                    guarantee compliance and prevent issues.
                  </p>
                </foreignObject>
              </g>
            </svg>
          )}
        </div>
      )}
    </div>
  );
};
